import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { FC } from "react";
import "./fonts";

export type SummaryPDFRow = {
	label: string;
	/** pre-formatted amount string, e.g. "82,112.67" (no currency symbol). Omitted for sections. */
	amount?: string;
	count?: number;
	/** emphasize as the bottom-line figure (Net) */
	highlight?: boolean;
	/** render as a section header (e.g. a status group) — bold label, no amount, optional count */
	section?: boolean;
	/** per-row currency code override (reports mix currencies); falls back to currencyPrefix */
	currencyPrefix?: string;
};

export type SummaryPDFProps = {
	title?: string;
	periodFrom: string;
	periodTo: string;
	rows: SummaryPDFRow[];
	currencyPrefix?: string;
	generatedAt?: string;
};

export const SummaryPDF: FC<SummaryPDFProps> = ({
	title = msg("transactionsSummary"),
	periodFrom,
	periodTo,
	rows,
	currencyPrefix = "EUR",
	generatedAt,
}) => { useLocale(); return ((
	<Document>
		<Page size="A4" style={s.page}>
			<View style={s.docHeader}>
				<Text style={s.docTitle}>{title}</Text>
				<Text style={s.docDate}>{generatedAt ?? new Date().toISOString().slice(0, 10)}</Text>
			</View>

			<View style={s.periodBlock}>
				<Text style={s.periodLabel}>{msg("period_2c05c5b9")}</Text>
				<Text style={s.periodValue}>{periodFrom} → {periodTo}</Text>
			</View>

			<View style={s.table}>
				{rows.map((r, i) => r.section ? (
					<View key={i} style={[s.row, s.sectionRow, i === rows.length - 1 ? s.rowLast : {}]}>
						<Text style={s.sectionLabel}>{r.label.toUpperCase()}</Text>
						{r.count !== undefined && (
							<Text style={s.cellCount}>{r.count.toLocaleString()} {r.count === 1 ? "report" : "reports"}</Text>
						)}
					</View>
				) : (
					<View key={i} style={[s.row, i === rows.length - 1 ? s.rowLast : {}, r.highlight ? s.rowHighlight : {}]}>
						<Text style={[s.cellLabel, r.highlight ? s.cellLabelHighlight : {}]}>{r.label.toUpperCase()}</Text>
						<View style={s.cellRight}>
							<Text style={[s.cellAmount, r.highlight ? s.cellAmountHighlight : {}]}>
								<Text style={s.currency}>{(r.currencyPrefix ?? currencyPrefix)} </Text>{r.amount}
							</Text>
							{r.count !== undefined && (
								<Text style={s.cellCount}>{r.count.toLocaleString()}</Text>
							)}
						</View>
					</View>
				))}
			</View>
		</Page>
	</Document>
)); };

const s = StyleSheet.create({
	page: {
		paddingTop: 28,
		paddingHorizontal: 28,
		paddingBottom: 40,
		fontFamily: "Euclid",
		fontSize: 9,
		color: "#1a1a1a",
	},
	docHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "flex-end",
		borderBottom: "1.5pt solid #1a1a1a",
		paddingBottom: 6,
		marginBottom: 12,
	},
	docTitle: { fontSize: 16, fontWeight: 700 },
	docDate: { fontSize: 9, color: "#666" },

	periodBlock: {
		backgroundColor: "#f2f4f7",
		borderRadius: 4,
		padding: "8pt 12pt",
		marginBottom: 16,
	},
	periodLabel: { fontSize: 7, color: "#94a3b8", letterSpacing: 0.6, fontWeight: 700 },
	periodValue: { fontSize: 11, fontWeight: 500, marginTop: 2 },

	table: { border: "0.5pt solid #e2e8f0", borderRadius: 4 },
	row: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "8pt 12pt",
		borderBottom: "0.5pt solid #eef2f6",
	},
	rowLast: { borderBottom: "none" },
	rowHighlight: { backgroundColor: "#f0fdfa" },
	sectionRow: { backgroundColor: "#f8fafc" },
	sectionLabel: { fontSize: 9, fontWeight: 700, color: "#334155", letterSpacing: 0.5 },

	cellLabel: { fontSize: 9, color: "#64748b", fontWeight: 500, letterSpacing: 0.4 },
	cellLabelHighlight: { color: "#0f766e", fontWeight: 700 },

	cellRight: { flexDirection: "row", alignItems: "baseline" },
	cellAmount: { fontSize: 11, fontWeight: 700, color: "#1e293b" },
	cellAmountHighlight: { color: "#0f766e" },
	currency: { color: "#94a3b8", fontWeight: 400 },
	cellCount: { fontSize: 8, color: "#94a3b8", marginLeft: 6 },
});
